import * as React from "react";
import { Box, Button, Stack, Typography, useColorScheme } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { goTo } from "@/lib/platform";
import { hasDashboardAccess, isOnboardingIncomplete, useOnboardingState } from "@/hooks/api/use-onboarding";

import { useAuth } from "../core/auth-provider";
import { AccountPicker } from "./account-picker";
import { Logo } from "../core/logo";
import { logoColors } from "../dashboard/layout/vertical/side-nav";

export interface SmbGuardProps {
	children: React.ReactNode;
	// The wizard itself: unfinished setup belongs here, and a live account does not.
	allowUnfinishedSetup?: boolean;
}

/**
 * Auth guard for the business app.
 *
 * Deliberately separate from the shared `AuthGuard`, which is coupled to
 * `dashboardConfig.navItems` / enterprise permissions. This guard only checks:
 * - a token exists (otherwise redirect to /sign-in),
 * - an account is entered (otherwise the user picks one here),
 * - the active account is an SMB account (otherwise redirect to the dashboard),
 * - the user is not a super admin (super admins always use the enterprise app),
 * - setup is finished (otherwise resume the wizard at /onboarding); the wizard sends a live account home.
 */
export function SmbGuard({ children, allowUnfinishedSetup = false }: SmbGuardProps): React.JSX.Element | null {
	const { isLoading, isAuthenticated, hasLoadError, getMe, setUser, setPermissions, account, user } = useAuth();

	const { colorScheme = "light" } = useColorScheme();
	const logoColor = logoColors[colorScheme]["blend_in"];

	const navigate = useNavigate();

	// Super admins are never SMB-routed — they manage SMB/platform accounts
	// through the normal enterprise app.
	const isSuperAdmin = Boolean(user?.isSuperAdmin);

	// Several accounts and none entered yet: the choice decides which app the user belongs in.
	const isChoosingAccount = isAuthenticated && !account && !isSuperAdmin;
	const isSmbAccount = isAuthenticated && account?.type === "smb" && !isSuperAdmin;
	const onboarding = useOnboardingState(false, isSmbAccount);
	const onboardingChecking = isSmbAccount && onboarding.isPending;
	const setupRedirect = isSmbAccount
		? allowUnfinishedSetup
			? hasDashboardAccess(onboarding.data)
				? "/"
				: null
			: isOnboardingIncomplete(onboarding.data)
				? "/onboarding"
				: null
		: null;

	React.useEffect(() => {
		if (!localStorage.getItem("token")) {
			setUser(null);
			setPermissions({});
			navigate("/sign-in");
		}
	}, [navigate, setPermissions, setUser]);

	React.useEffect(() => {
		if (isLoading || !isAuthenticated) {
			return;
		}

		if (isChoosingAccount) {
			return;
		}

		if (account?.type !== "smb" || isSuperAdmin) {
			// Enterprise users have no business inside the SMB shell, and super
			// admins always belong in the enterprise app.
			goTo(navigate, "admin", "/", { replace: true });
		}
	}, [account, isAuthenticated, isChoosingAccount, isLoading, isSuperAdmin, navigate]);

	React.useEffect(() => {
		if (setupRedirect) navigate(setupRedirect, { replace: true });
	}, [setupRedirect, navigate]);

	if (hasLoadError) {
		// Same case AuthGuard handles: the token is still good but /me failed (backend deploying,
		// network down). Without this the SMB shell sits on its loader forever, with no way out.
		const signOut = () => {
			localStorage.removeItem("token");
			localStorage.removeItem("user");
			navigate("/sign-in");
		};

		return (
			<Box
				sx={{
					width: "100%",
					height: "100%",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: 4,
					p: 2,
				}}
			>
				<Logo height={60} width={222} color={logoColor} />
				<Stack spacing={2} sx={{ alignItems: "center", maxWidth: 360, textAlign: "center" }}>
					<Typography>We couldn’t load your profile. Check your connection and try again.</Typography>
					<Stack direction="row" spacing={1}>
						<Button variant="contained" onClick={() => getMe()}>
							Try again
						</Button>
						<Button variant="text" onClick={signOut}>
							Sign out
						</Button>
					</Stack>
				</Stack>
			</Box>
		);
	}

	if (isChoosingAccount && !isLoading) {
		return <AccountPicker />;
	}

	if (isLoading || !isAuthenticated || !isSmbAccount || onboardingChecking || setupRedirect) {
		return (
			<Box
				sx={{
					width: "100%",
					height: "100%",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: 4,
				}}
			>
				<Logo height={60} width={222} color={logoColor} />
				<Box className="page-loader" />
			</Box>
		);
	}

	return <React.Fragment>{children}</React.Fragment>;
}
